import { useState, useRef } from "react";
import './index.css';

// ─── API KEY ────────────────────────────────────────────────
// Set REACT_APP_ANTHROPIC_API_KEY in Vercel environment variables
const ANTHROPIC_API_KEY = process.env.REACT_APP_ANTHROPIC_API_KEY || "";

// ─── MODEL ──────────────────────────────────────────────────
// IMPORTANT: Always use Haiku — Sonnet is ~25x more expensive
const MODEL = "claude-haiku-4-5-20251001";

// Hard safety check — if this isn't Haiku, nothing works
const ALLOWED_MODEL = "claude-haiku-4-5-20251001";
if (MODEL !== ALLOWED_MODEL) {
  throw new Error(`BLOCKED: Wrong model "${MODEL}" — only Haiku is allowed. Check your code.`);
}

// ─── AFFILIATE CODES ────────────────────────────────────────
// Fill these in as you sign up for each program
const AFFILIATE_CODES = {
  amazon:         null,
  campingworld:   null,
  rvupgradestore: null,
  etrailer:       null,
};

// ─── RETAIL STORES ──────────────────────────────────────────
const STORES = [
  {
    name: "Panther RV Products",
    color: "#4CAF7D",
    url: (pn) => `https://www.pantherrvproducts.com/search?q=${encodeURIComponent(pn)}`,
  },
  {
    name: "RV Upgrade Store",
    color: "#E8A030",
    url: (pn) => `https://www.rvupgradestore.com/search?q=${encodeURIComponent(pn)}${AFFILIATE_CODES.rvupgradestore ? `&affcode=${AFFILIATE_CODES.rvupgradestore}` : ""}`,
  },
  {
    name: "Camping World",
    color: "#C0392B",
    url: (pn) => `https://www.campingworld.com/search?q=${encodeURIComponent(pn)}${AFFILIATE_CODES.campingworld ? `&affid=${AFFILIATE_CODES.campingworld}` : ""}`,
  },
  {
    name: "eTrailer",
    color: "#8E44AD",
    url: (pn) => `https://www.etrailer.com/search.aspx?q=${encodeURIComponent(pn)}${AFFILIATE_CODES.etrailer ? `&aff=${AFFILIATE_CODES.etrailer}` : ""}`,
  },
  {
    name: "Amazon",
    color: "#E47911",
    warning: "⚠️ Verify OEM vs aftermarket before ordering",
    url: (pn) => `https://www.amazon.com/s?k=${encodeURIComponent(pn)}${AFFILIATE_CODES.amazon ? `&tag=${AFFILIATE_CODES.amazon}` : ""}`,
  },
  {
    name: "Search Google",
    color: "#4285F4",
    url: (pn) => `https://www.google.com/search?q=${encodeURIComponent(pn + " RV part buy")}`,
  },
];

// ─── DRIVE LIBRARY ──────────────────────────────────────────
// Maps keywords to specific PDFs — only surface the most relevant doc
// file: { name, id, desc, keywords[] }
const LIBRARY = [
  // SUBURBAN FURNACES
  { brand: "suburban", keywords: ["furnace","sf","nt","sh","shd","sfv","sf-","nt-","sh-","35421","heater","burner","sail switch","control board","igniter","electrode","limit switch"], name: "Suburban Furnace Master Service Manual", id: "1rvS_placeholder_SF_NT", desc: "SF/NT/SH/SFV series service and parts" },
  { brand: "suburban", keywords: ["nautilus","water heater","sw","sw6","sw10","sw12"], name: "Suburban Nautilus Parts List", id: "11lDZl6Mey8ojogvRskFF1FV3UKu_uuIr", desc: "Nautilus series parts diagram" },
  { brand: "suburban", keywords: ["tankless","st60","iw60","st-60","iw-60"], name: "Suburban ST Tankless Service Manual", id: "1MrZAE8aRucBoMxflsSUJrsGtakqJl_ag", desc: "ST 60 tankless water heater" },

  // ATWOOD / DOMETIC FURNACES
  { brand: "atwood", keywords: ["furnace","hydroflame","hydro flame","af","df","dfmd","afmd","7916","8516","8531","8520","gf-","burner","sail switch","control board","igniter","limit switch"], name: "Atwood HydroFlame Furnace Service Manual", id: "1IYKdKDkv33kNhC-Mc8oSVoB2KPEPJOIj", desc: "Classic and HydroFlame furnace series" },
  { brand: "atwood", keywords: ["furnace training","gf series","comfort"], name: "Atwood Furnace Training Manual", id: "1Gf1placeholder_training", desc: "GF series furnace training" },

  // ATWOOD / DOMETIC WATER HEATERS
  { brand: "atwood", keywords: ["water heater","gc6","gc10","g6a","g10a","gch","dhw","electrode","thermocouple","anode","burner assembly","igniter"], name: "Dometic Water Heater Service Manual", id: "1u3Oi7V3FDQAHmqy1Y76WhCMcaVHPhLv9", desc: "GC/G series 6 and 10 gallon water heaters" },

  // DOMETIC REFRIGERATORS
  { brand: "dometic", keywords: ["refrigerator","fridge","rm","rm1350","rm2","ndr","cooling unit","absorption"], name: "Dometic RM Series Refrigerator Service Manual", id: "1jUZjFgjN0fZ0Mbdjm_-2cHjS8ZSokvaT", desc: "RM series absorption refrigerators" },
  { brand: "dometic", keywords: ["cooling unit","replacement","install"], name: "Dometic Cooling Unit Install Guide", id: "1nmpwJsF8T2DPsORE-NVQGlWPNOhycLEU", desc: "Cooling unit replacement — many models" },

  // CUMMINS / ONAN
  { brand: "onan", keywords: ["ky","microquiet","3600","4000","carburetor","fuel pump","fuel filter","points","condenser","ignition","control board","starter","spec"], name: "Onan KY Service Manual", id: "1LNXhJ74sU1ds3A4FeDhChNZIYGopbi_M", desc: "KY MicroQuiet 3600/4000 service manual" },
  { brand: "onan", keywords: ["ky","parts","parts list","breakdown"], name: "Onan KY Parts Manual", id: "1mjJDmD_zCYF0hsz6DDStqV0O2JylWyqJ", desc: "KY generator parts catalog with diagrams" },
  { brand: "onan", keywords: ["hgjaa","hgjab","hgjac","hgjad","hgjae","hgjaf","rv generator","motorhome generator","aq generator"], name: "Onan HGJAA-HGJAF Service Manual", id: "1Uexv-XiyI_9wDnPrUuIUwv6OOoekPrWq", desc: "HGJAA through HGJAF series service manual" },

  // CAREFREE AWNINGS
  { brand: "carefree", keywords: ["awning","arm","strut","travel'r","travlr","compass","latitude","longitude","altitude","eclipse","fiesta","spirit","rafter","fabric"], name: "Carefree Travel'r Service Manual", id: "1X0UIPVUJp5DHwwc7ZoZ00Nr3q-xaDNxX", desc: "Full service manual for Travel'r awnings" },

  // LIPPERT SLIDES
  { brand: "lippert", keywords: ["slide","schwintek","in-wall","electric slide","motor slide"], name: "Lippert Master Slide-Out Owners Manual", id: "1BHsnb7of8Oh1cCV5N5ETGWtz_o-FxHO-", desc: "Comprehensive guide — all slide systems" },
  { brand: "lippert", keywords: ["hydraulic slide","hydraulic slideout"], name: "Lippert Hydraulic Slideout System", id: "11piemrugtI73DD6dolhBY8mpR9gfXpXo", desc: "Hydraulic slide service and parts" },

  // LIPPERT LEVELING
  { brand: "lippert", keywords: ["level-up","levelup","leveling","auto level"], name: "Lippert Level-Up Automatic Leveling Manual", id: "1XN3BfmzhT8HLuhYbQtvB7ZZk0trAeb2m", desc: "Level-Up owners manual" },
  { brand: "lippert", keywords: ["motorized leveling","hydraulic leveling","ground control","gc3"], name: "LCI Motorized Leveling Owners Manual", id: "18TShhys8vuY4a26wSrGzkIkjkMdWE8Wr", desc: "Motorized and hydraulic leveling systems" },

  // BAL
  { brand: "bal", keywords: ["exact-slide","exact slide","accu-slide","norco","slide motor","r25078","r25079"], name: "BAL Exact-Slide Operation Manual", id: "1UczDJAfBnakzXsAwxihJLGvbK8NHwVXe", desc: "Full service — includes motor part numbers" },

  // COLEMAN-MACH AC
  { brand: "coleman", keywords: ["ac","air conditioner","mach","capacitor","control board","fan motor","compressor","brisk","penguin"], name: "Coleman-Mach Service Manual", id: "1mIitS_vh_SsqKQnPm_JYLgILpOIdtTcd", desc: "Coleman-Mach AC service documentation" },

  // NORCOLD
  { brand: "norcold", keywords: ["refrigerator","fridge","norcold","2118","polar","n620","n621","n300","n410","cooling unit","control board","thermistor"], name: "Norcold 2118 Service Manual", id: "1SOGfm2aqd-jo_txdoduuaoUXwFtRQTTQ", desc: "Norcold 2118 and Polar series" },

  // XANTREX / MAGNUM
  { brand: "xantrex", keywords: ["freedom","inverter","charger","converter","xantrex"], name: "Xantrex Freedom 458 Owners Manual", id: "1SOGfm2aqd-jo_txdoduuaoUXwFtRQTTQ", desc: "Freedom 458 series" },
  { brand: "magnum", keywords: ["ags","auto gen start","generator start","magnum"], name: "Magnum AGS Manual", id: "18Q9c6cOs_H72_CdCkZ8GjTKo-zygn65q", desc: "Automatic generator start system" },
];

// Find the single best matching library entry
function findBestLibraryMatch(brand, component, query) {
  const text = `${brand} ${component} ${query}`.toLowerCase();

  // Score each entry
  let best = null;
  let bestScore = 0;

  for (const entry of LIBRARY) {
    // Must match brand first
    if (!text.includes(entry.brand)) continue;

    // Count keyword matches
    let score = 0;
    for (const kw of entry.keywords) {
      if (text.includes(kw)) score += kw.length; // longer keywords score higher
    }

    if (score > bestScore) {
      bestScore = score;
      best = entry;
    }
  }

  // Only return if we have a meaningful match
  return bestScore > 3 ? best : null;
}

// ─── SYSTEM PROMPT ──────────────────────────────────────────
const SYSTEM_PROMPT = `You are an expert RV parts technician with 20+ years experience. The user is a professional mobile RV tech ordering parts from a distributor. Wrong part numbers cost them money and time.

CRITICAL: Return ONLY valid JSON. No preamble, no markdown, no text outside the JSON.

ACCURACY RULES:
- Only return part numbers you are genuinely confident about
- 80%+ confident = include with caveat note if needed
- Under 80% confident = set "found": false. Do NOT guess.
- Returning found:false is ALWAYS better than a wrong part number
- Never fabricate or approximate part numbers

CONFIDENCE DEFINITIONS:
- "high": Confirmed for this exact model in service documentation. 95%+ certain.
- "medium": Very likely correct but cannot confirm for this specific variant. 70-94% certain.
- "low": Recognize the component but cannot confidently ID the part number — consider found:false instead.

CUMMINS/ONAN MODEL NAMING:
Onan uses base model + spec letter. The spec letter determines which parts apply.
- KY series specs A through N — spec letter changes carburetor, points, control board, fuel pump
- KY Spec J carburetor: 146-0657 kit, float 149-2375
- KY Spec L carburetor: 146-0803 kit  
- KY fuel pump (all specs): 149-2320
- KY fuel filter (all specs): 149-2457
- KY points: 166-0761, condenser: 166-0762
- HGJAB, HGJAC, HGJAE are separate models — not specs of the same model
- Always use the spec letter to narrow down the correct part

BRAND IDENTIFICATION:
- Suburban furnaces: SF/NT/SH/SHD/SFV series, or model ending in SU (35421SU = Suburban SF-35)
- Suburban water heaters: SW/GW series
- Carefree of Colorado: Compass, Longitude, Altitude, Latitude, Travel'r, Fiesta, Eclipse, Spirit
- Lippert/LCI: Schwintek, Power Gear, Level-Up, OneControl, Furrion, EuroLoft, Girard, Kwikee, SlimRack, Solera
- BAL/Norco: Exact-Slide, Accu-Slide
- Cummins/Onan: KY, KYD, HGJAA-HGJAF, BGE, BGEL, NHE, QD series
- Dometic AC: Duo-Therm, Blizzard, Penguin, Brisk Air (model numbers start 47xxx or 57xxx)
- Dometic fridges: RM series
- Atwood/Dometic furnaces: AF/DF/AFMD/DFMD series, or HydroFlame
- Atwood/Dometic water heaters: GC/GCH/G series
- Norcold: N/NA/NXA series
- Coleman-Mach: Mach 1/3/8/10/15 (model numbers start 47xxx or 48xxx)

TERMINOLOGY TRANSLATIONS (field terms → catalog terms):
- "thermocouple" on DSI water heater = electrode / spark probe / igniter assembly
- "sail switch" = air proving switch / pressure switch
- "board" or "control board" = circuit board / control module
- "run cap" or "run capacitor" = motor run capacitor
- "start cap" = motor start capacitor / hard start kit
- "limit switch" = high limit switch / thermal cutout
- "gas valve" = combination gas valve / dual solenoid gas valve
- "anode rod" = magnesium anode rod

Return ONLY this JSON:
{
  "found": true,
  "brand_identified": "Exact manufacturer name or null",
  "component_description": "One sentence: what this part is and what it does",
  "part_numbers": [
    { "number": "EXACT-PART-NUMBER", "note": "spec/variant qualifier or null" }
  ],
  "crossover_models": ["other models confirmed to use this exact part"],
  "adjacent_parts": [
    { "part": "part name", "reason": "why a tech needs this on the same job", "part_number": "confirmed number or null" }
  ],
  "caveats": "Important variant/supersession warnings. null if none.",
  "confidence": "high or medium or low",
  "confidence_note": "One honest sentence explaining your confidence level"
}`;

// ─── COMPONENT ──────────────────────────────────────────────
export default function RVPartsLookup() {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null); // 'tool' | 'input'
  const [copied, setCopied] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [feedbackNotes, setFeedbackNotes] = useState("");
  const [feedbackPartNum, setFeedbackPartNum] = useState("");
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [feedbackSending, setFeedbackSending] = useState(false);
  const inFlight = useRef(false);

  const FEEDBACK_URL = "https://script.google.com/macros/s/AKfycbyh4SjOSGmq1Y8g_HalPGe3Ng8Ne6cYwu_VuQm327v06ZR4K3lAiVvYOb4lD9PeBJn6XQ/exec";

  const copy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const submitFeedback = (helpful) => {
    setFeedback(helpful);
    if (helpful === "up") sendFeedback("up", "", "");
  };

  const sendFeedback = async (helpful, notes, actualPart) => {
    setFeedbackSending(true);
    try {
      await fetch(FEEDBACK_URL, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query,
          brand: result?.brand_identified || "",
          partNumbers: result?.part_numbers?.map(p => p.number).join(", ") || "",
          confidence: result?.confidence || "",
          helpful: helpful === "up" ? "Yes" : "No",
          notes: notes || "",
          actualPartNumber: actualPart || "",
        }),
      });
      setFeedbackSent(true);
    } catch {
      setFeedbackSent(true);
    } finally {
      setFeedbackSending(false);
    }
  };

  const handleSearch = async () => {
    if (!query.trim() || inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    setResult(null);
    setError(null);
    setErrorType(null);
    setFeedback(null);
    setFeedbackNotes("");
    setFeedbackPartNum("");
    setFeedbackSent(false);

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 30000);

    try {
      if (!ANTHROPIC_API_KEY) throw new Error("NO_KEY");
      if (MODEL !== ALLOWED_MODEL) throw new Error("WRONG_MODEL");

      const response = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": ANTHROPIC_API_KEY,
          "anthropic-version": "2023-06-01",
          "anthropic-dangerous-direct-browser-access": "true",
        },
        signal: controller.signal,
        body: JSON.stringify({
          model: MODEL,
          max_tokens: 1000,
          system: SYSTEM_PROMPT,
          messages: [{ role: "user", content: query }],
        }),
      });

      clearTimeout(timeout);

      if (response.status === 401) throw new Error("NO_KEY");
      if (response.status === 429) throw new Error("RATE_LIMIT");
      if (response.status === 529 || response.status === 503) throw new Error("OVERLOADED");
      if (!response.ok) throw new Error(`API_${response.status}`);

      const data = await response.json();
      if (data.error) throw new Error("API_ERROR");

      const text = data.content?.find(b => b.type === "text")?.text || "";
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (!jsonMatch) throw new Error("PARSE_ERROR");

      setResult(JSON.parse(jsonMatch[0]));
    } catch (e) {
      clearTimeout(timeout);
      const msg = e.message || "";
      if (e.name === "AbortError") {
        setErrorType("tool");
        setError("The lookup timed out. Our AI is taking too long to respond right now.");
      } else if (msg === "NO_KEY" || msg.includes("401")) {
        setErrorType("tool");
        setError("The tool is temporarily offline — the API key needs attention. We've been notified.");
      } else if (msg === "WRONG_MODEL") {
        setErrorType("tool");
        setError("Configuration error — wrong AI model detected. Tool blocked to prevent unexpected charges.");
      } else if (msg === "RATE_LIMIT") {
        setErrorType("tool");
        setError("The tool is temporarily at capacity. Please try again in a minute.");
      } else if (msg === "OVERLOADED") {
        setErrorType("tool");
        setError("The AI service is temporarily overloaded. Please try again in a moment.");
      } else if (msg === "PARSE_ERROR") {
        setErrorType("input");
        setError("The AI returned an unexpected response. Try rephrasing with brand, model number, and part name.");
      } else {
        setErrorType("tool");
        setError("The lookup tool encountered an error. Please try again.");
      }
    } finally {
      setLoading(false);
      inFlight.current = false;
    }
  };

  const confColor = (c) => ({
    high: "#4CAF7D",
    medium: "#E8A030",
    low: "#E05C5C",
  }[c] || "#666");

  const libraryMatch = result
    ? findBestLibraryMatch(result.brand_identified || "", result.component_description || "", query)
    : null;

  const primaryPartNum = result?.part_numbers?.[0]?.number;

  // ─── STYLES ─────────────────────────────────────────────
  const S = {
    // Layout
    page: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      background: "#0f1117",
      minHeight: "100vh",
      color: "#E8E6E0",
    },
    header: {
      background: "#0f1117",
      borderBottom: "1px solid #1e2130",
      padding: "14px 20px",
      position: "sticky",
      top: 0,
      zIndex: 10,
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    logoMark: {
      width: "32px",
      height: "32px",
      background: "#E8A030",
      borderRadius: "6px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "14px",
      fontWeight: "800",
      color: "#0f1117",
      flexShrink: 0,
    },
    logoText: {
      display: "flex",
      flexDirection: "column",
    },
    logoSuite: {
      fontSize: "10px",
      letterSpacing: "3px",
      color: "#E8A030",
      textTransform: "uppercase",
      fontWeight: "600",
      lineHeight: 1,
    },
    logoTool: {
      fontSize: "16px",
      fontWeight: "700",
      color: "#E8E6E0",
      lineHeight: 1.2,
    },
    body: {
      padding: "20px 16px 60px",
      maxWidth: "600px",
      margin: "0 auto",
    },

    // Input
    textarea: {
      width: "100%",
      background: "#161820",
      border: "1px solid #2a2d3e",
      borderRadius: "10px",
      color: "#E8E6E0",
      fontFamily: "inherit",
      fontSize: "16px",
      padding: "16px",
      resize: "none",
      boxSizing: "border-box",
      outline: "none",
      lineHeight: "1.6",
      marginBottom: "12px",
      transition: "border-color 0.2s",
    },
    btn: (dis) => ({
      width: "100%",
      background: "#E8A030",
      color: "#0f1117",
      border: "none",
      borderRadius: "10px",
      padding: "18px",
      fontSize: "15px",
      fontFamily: "inherit",
      fontWeight: "700",
      letterSpacing: "1px",
      textTransform: "uppercase",
      cursor: dis ? "not-allowed" : "pointer",
      marginBottom: "20px",
      opacity: dis ? 0.5 : 1,
      transition: "all 0.15s",
    }),

    // Cards
    card: {
      background: "#161820",
      borderRadius: "10px",
      border: "1px solid #2a2d3e",
      overflow: "hidden",
    },
    cardLabel: {
      padding: "10px 16px",
      borderBottom: "1px solid #2a2d3e",
      fontSize: "11px",
      letterSpacing: "2px",
      color: "#E8A030",
      textTransform: "uppercase",
      fontWeight: "700",
      background: "#13151f",
    },

    // Buttons
    copyBtn: (active) => ({
      background: active ? "#4CAF7D" : "#1e2130",
      color: active ? "#0f1117" : "#888",
      border: "1px solid",
      borderColor: active ? "#4CAF7D" : "#2a2d3e",
      borderRadius: "6px",
      padding: "8px 16px",
      fontSize: "13px",
      fontFamily: "inherit",
      fontWeight: "600",
      cursor: "pointer",
      flexShrink: 0,
      transition: "all 0.15s",
    }),
    storeLink: (color) => ({
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      background: "#1a1c27",
      borderLeft: `3px solid ${color}`,
      borderRadius: "6px",
      padding: "13px 14px",
      color: "#C8C6C0",
      fontSize: "15px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "background 0.15s",
    }),
  };

  // ─── RENDER ─────────────────────────────────────────────
  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div style={S.logoMark}>RV</div>
        <div style={S.logoText}>
          <span style={S.logoSuite}>RVtechSuite</span>
          <span style={S.logoTool}>Parts Lookup</span>
        </div>
      </div>

      <div style={S.body}>
        {/* Search Input */}
        <textarea
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSearch();
            }
          }}
          placeholder={"Describe what you need...\n\ne.g. \"Onan KY Spec L carburetor\"\nor \"Suburban SF-35 sail switch\"\nor \"Coleman-Mach 48203 run capacitor\""}
          rows={3}
          style={S.textarea}
        />

        <button
          onClick={handleSearch}
          disabled={loading || !query.trim()}
          style={S.btn(loading || !query.trim())}
        >
          {loading ? "Searching..." : "Find Part"}
        </button>

        {/* Loading indicator */}
        {loading && (
          <div style={{ textAlign: "center", color: "#444", fontSize: "13px", letterSpacing: "1px", marginBottom: "20px" }}>
            Identifying part...
          </div>
        )}

        {/* Error states */}
        {error && (
          <div style={{
            background: "#1a1015",
            borderLeft: `4px solid ${errorType === "tool" ? "#E05C5C" : "#E8A030"}`,
            borderRadius: "8px",
            padding: "16px",
            marginBottom: "16px",
          }}>
            {errorType === "tool" ? (
              <>
                <div style={{ color: "#E05C5C", fontWeight: "700", fontSize: "13px", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>
                  Tool Error
                </div>
                <div style={{ color: "#999", fontSize: "14px", lineHeight: "1.6", marginBottom: "12px" }}>
                  {error} This issue has been noted. In the meantime:
                </div>
                <a
                  href={`https://www.google.com/search?q=${encodeURIComponent(query + " RV part number")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#4285F4", fontSize: "14px", fontWeight: "600" }}
                >
                  → Search Google for this part ↗
                </a>
              </>
            ) : (
              <>
                <div style={{ color: "#E8A030", fontWeight: "700", fontSize: "13px", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>
                  Part Not Identified
                </div>
                <div style={{ color: "#999", fontSize: "14px", lineHeight: "1.6", marginBottom: "4px" }}>
                  {error}
                </div>
                <div style={{ color: "#555", fontSize: "13px" }}>
                  Try: brand + model number + part name (e.g. "Onan KY Spec J carburetor kit")
                </div>
              </>
            )}
          </div>
        )}

        {/* Results */}
        {result && (
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>

            {/* Brand pill */}
            {result.brand_identified && (
              <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", background: "#161820", border: "1px solid #2a2d3e", borderRadius: "6px", padding: "8px 14px", alignSelf: "flex-start" }}>
                <span style={{ fontSize: "11px", letterSpacing: "2px", color: "#E8A030", textTransform: "uppercase", fontWeight: "700" }}>Brand</span>
                <span style={{ fontSize: "14px", color: "#E8E6E0", fontWeight: "600" }}>{result.brand_identified}</span>
              </div>
            )}

            {/* Confidence */}
            <div style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "10px",
              background: "#161820",
              borderLeft: `3px solid ${confColor(result.confidence)}`,
              borderRadius: "8px",
              padding: "12px 14px",
            }}>
              <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: confColor(result.confidence), flexShrink: 0, marginTop: "4px" }} />
              <div>
                <span style={{ color: confColor(result.confidence), fontWeight: "700", textTransform: "uppercase", fontSize: "12px", letterSpacing: "1px" }}>
                  {result.confidence} confidence
                </span>
                {result.confidence_note && (
                  <div style={{ color: "#666", fontSize: "13px", marginTop: "3px", lineHeight: "1.5" }}>{result.confidence_note}</div>
                )}
              </div>
            </div>

            {/* Component description */}
            {result.component_description && (
              <div style={{ color: "#888", fontSize: "14px", fontStyle: "italic", paddingLeft: "2px", lineHeight: "1.6" }}>
                {result.component_description}
              </div>
            )}

            {/* Part numbers */}
            {result.found && result.part_numbers?.length > 0 && (
              <div style={S.card}>
                <div style={S.cardLabel}>Part Number{result.part_numbers.length > 1 ? "s" : ""}</div>
                {result.part_numbers.map((pn, i) => (
                  <div key={i} style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "16px",
                    borderBottom: i < result.part_numbers.length - 1 ? "1px solid #1e2130" : "none",
                    gap: "12px",
                  }}>
                    <div>
                      <div style={{ fontSize: "28px", fontWeight: "800", color: "#E8E6E0", letterSpacing: "3px", fontFamily: "monospace" }}>
                        {pn.number}
                      </div>
                      {pn.note && <div style={{ fontSize: "12px", color: "#555", marginTop: "3px" }}>{pn.note}</div>}
                    </div>
                    <button onClick={() => copy(pn.number, `pn-${i}`)} style={S.copyBtn(copied === `pn-${i}`)}>
                      {copied === `pn-${i}` ? "Copied ✓" : "Copy"}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Buy links */}
            {result.found && primaryPartNum && (
              <div style={S.card}>
                <div style={S.cardLabel}>Find to Buy</div>
                <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: "8px" }}>
                  {STORES.map((store, i) => (
                    <div key={i}>
                      <a
                        href={store.url(primaryPartNum)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={S.storeLink(store.color)}
                      >
                        <span>{store.name}</span>
                        <span style={{ color: "#333", fontSize: "16px" }}>↗</span>
                      </a>
                      {store.warning && (
                        <div style={{ fontSize: "12px", color: "#7a6030", padding: "4px 14px 0", fontStyle: "italic" }}>
                          {store.warning}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Schematic Library — only show if we have a specific match */}
            {libraryMatch && (
              <div style={S.card}>
                <div style={{ ...S.cardLabel, color: "#4CAF7D" }}>📄 Relevant Schematic</div>
                <a
                  href={`https://drive.google.com/file/d/${libraryMatch.id}/view`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: "block", padding: "16px", textDecoration: "none" }}
                >
                  <div style={{ fontSize: "15px", fontWeight: "600", color: "#E8E6E0", marginBottom: "4px" }}>
                    {libraryMatch.name}
                  </div>
                  <div style={{ fontSize: "13px", color: "#666" }}>{libraryMatch.desc}</div>
                  <div style={{ fontSize: "12px", color: "#4CAF7D", marginTop: "8px", fontWeight: "600" }}>
                    Open PDF ↗
                  </div>
                </a>
              </div>
            )}

            {/* Crossover models */}
            {result.crossover_models?.length > 0 && (
              <div style={{ background: "#111a14", borderLeft: "3px solid #4CAF7D", borderRadius: "8px", padding: "14px 16px" }}>
                <div style={{ fontSize: "11px", letterSpacing: "2px", color: "#4CAF7D", textTransform: "uppercase", fontWeight: "700", marginBottom: "8px" }}>
                  Same Part Fits
                </div>
                <div style={{ fontSize: "14px", color: "#C8C6C0", lineHeight: "1.8" }}>
                  {result.crossover_models.join(" · ")}
                </div>
              </div>
            )}

            {/* Adjacent parts */}
            {result.adjacent_parts?.length > 0 && (
              <div style={S.card}>
                <div style={S.cardLabel}>While You're In There</div>
                {result.adjacent_parts.map((p, i) => (
                  <div key={i} style={{
                    padding: "14px 16px",
                    borderBottom: i < result.adjacent_parts.length - 1 ? "1px solid #1e2130" : "none",
                    display: "flex",
                    gap: "12px",
                  }}>
                    <div style={{ color: "#E8A030", flexShrink: 0, fontSize: "16px", marginTop: "1px" }}>→</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginBottom: "4px" }}>
                        <span style={{ fontWeight: "700", color: "#E8E6E0", fontSize: "15px" }}>{p.part}</span>
                        {p.part_number && (
                          <button
                            onClick={() => copy(p.part_number, `adj-${i}`)}
                            style={{
                              background: copied === `adj-${i}` ? "#4CAF7D" : "#1e2130",
                              color: copied === `adj-${i}` ? "#0f1117" : "#888",
                              border: "1px solid #2a2d3e",
                              borderRadius: "4px",
                              padding: "2px 10px",
                              fontSize: "12px",
                              fontFamily: "monospace",
                              cursor: "pointer",
                              fontWeight: "600",
                            }}
                          >
                            {copied === `adj-${i}` ? "✓" : p.part_number}
                          </button>
                        )}
                      </div>
                      <div style={{ fontSize: "13px", color: "#555" }}>{p.reason}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Caveats */}
            {result.caveats && (
              <div style={{ background: "#1a1800", borderLeft: "3px solid #E8A030", borderRadius: "8px", padding: "14px 16px" }}>
                <div style={{ fontSize: "11px", letterSpacing: "2px", color: "#E8A030", textTransform: "uppercase", fontWeight: "700", marginBottom: "6px" }}>
                  ⚠ Watch Out
                </div>
                <div style={{ fontSize: "14px", color: "#B8A060", lineHeight: "1.6" }}>{result.caveats}</div>
              </div>
            )}

            {/* Part not found */}
            {!result.found && (
              <div style={{ background: "#1a1015", borderLeft: "3px solid #E05C5C", borderRadius: "8px", padding: "16px" }}>
                <div style={{ fontWeight: "700", color: "#E05C5C", marginBottom: "8px", fontSize: "13px", textTransform: "uppercase", letterSpacing: "1px" }}>
                  Part Not Identified
                </div>
                <div style={{ color: "#888", fontSize: "14px", marginBottom: "8px", lineHeight: "1.6" }}>
                  {result.confidence_note}
                </div>
                <div style={{ color: "#444", fontSize: "13px" }}>
                  Try: brand · model number · component name · what it does
                </div>
              </div>
            )}

            {/* Feedback */}
            {!feedbackSent ? (
              <div style={{ background: "#161820", border: "1px solid #2a2d3e", borderRadius: "10px", padding: "16px" }}>
                <div style={{ fontSize: "14px", color: "#666", marginBottom: "12px" }}>Was this result helpful?</div>
                <div style={{ display: "flex", gap: "10px", marginBottom: feedback === "down" ? "14px" : "0" }}>
                  <button
                    onClick={() => submitFeedback("up")}
                    style={{
                      flex: 1, background: feedback === "up" ? "#4CAF7D" : "#1e2130",
                      color: feedback === "up" ? "#0f1117" : "#666",
                      border: "1px solid", borderColor: feedback === "up" ? "#4CAF7D" : "#2a2d3e",
                      borderRadius: "8px", padding: "12px", fontSize: "20px", cursor: "pointer",
                    }}
                  >👍</button>
                  <button
                    onClick={() => submitFeedback("down")}
                    style={{
                      flex: 1, background: feedback === "down" ? "#C0392B" : "#1e2130",
                      color: feedback === "down" ? "#fff" : "#666",
                      border: "1px solid", borderColor: feedback === "down" ? "#C0392B" : "#2a2d3e",
                      borderRadius: "8px", padding: "12px", fontSize: "20px", cursor: "pointer",
                    }}
                  >👎</button>
                </div>
                {feedback === "down" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    <input
                      placeholder="Correct part number (if you know it)"
                      value={feedbackPartNum}
                      onChange={e => setFeedbackPartNum(e.target.value)}
                      style={{ background: "#0f1117", border: "1px solid #2a2d3e", borderRadius: "6px", color: "#E8E6E0", fontFamily: "inherit", fontSize: "15px", padding: "12px", outline: "none" }}
                    />
                    <textarea
                      placeholder="What was wrong or what did you need?"
                      value={feedbackNotes}
                      onChange={e => setFeedbackNotes(e.target.value)}
                      rows={2}
                      style={{ background: "#0f1117", border: "1px solid #2a2d3e", borderRadius: "6px", color: "#E8E6E0", fontFamily: "inherit", fontSize: "15px", padding: "12px", outline: "none", resize: "none" }}
                    />
                    <button
                      onClick={() => sendFeedback("down", feedbackNotes, feedbackPartNum)}
                      disabled={feedbackSending}
                      style={{ background: feedbackSending ? "#1e2130" : "#E8A030", color: feedbackSending ? "#444" : "#0f1117", border: "none", borderRadius: "8px", padding: "14px", fontFamily: "inherit", fontSize: "14px", fontWeight: "700", cursor: feedbackSending ? "not-allowed" : "pointer" }}
                    >
                      {feedbackSending ? "Sending..." : "Send Feedback"}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: "center", color: "#4CAF7D", fontSize: "14px", fontWeight: "600", padding: "16px" }}>
                ✓ Feedback received — thank you
              </div>
            )}

          </div>
        )}

        {/* Empty state */}
        {!result && !loading && !error && (
          <div style={{ textAlign: "center", color: "#2a2d3e", fontSize: "13px", letterSpacing: "2px", marginTop: "8px" }}>
            BRAND · MODEL · COMPONENT
          </div>
        )}
      </div>
    </div>
  );
}
